import { useEffect, useMemo, useContext, type FC } from 'react';
import {
  FilterContext,
  PaginationContext,
  SearchContext,
  SortContext,
} from 'dash-ui';
import { useGetPropertiesQuery } from 'gql/generated.graphql';

import type { PropertiesDataProviderProps } from './PropertiesData.interfaces';
import PropertiesDataContext from './PropertiesDataContext';

const PropertiesDataProvider: FC<PropertiesDataProviderProps> = ({
  children,
  filter = {},
}) => {
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { state: filterContext } = useContext(FilterContext);

  const { skip, limit } = getRefetchProps(paginationState);
  const searchByName = searchSlug.length > 2 ? searchSlug : null;
  const statuses = filterContext.statuses || [];
  const sortQuery = sort.fieldName
    ? [sort]
    : [{ fieldName: 'createdAt', sortOrder: -1 }];
  const {
    data: result,
    refetch,
    loading,
  } = useGetPropertiesQuery({
    variables: {
      sort: sortQuery,
      pagination: { skip, limit },
      filter: {
        statuses,
        searchByName,
        ...filter,
      },
    },
  });
  const { data, total } = useMemo(
    () => ({
      data: result?.getProperties?.collection || [],
      total: result?.getProperties?.totalCount || 0,
    }),
    [result]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    refetch({
      sort: sortQuery,
      pagination: { skip, limit },
      filter: {
        statuses,
        searchByName,
        ...filter,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, sort, searchSlug, statuses]);

  return (
    <PropertiesDataContext.Provider value={{ data, loading }}>
      {children}
    </PropertiesDataContext.Provider>
  );
};

export default PropertiesDataProvider;
